/**
 * Shared post-auth steps for Pro signup.
 *
 * Call only after email is verified (Confirm Signup OTP or confirm link).
 * Then:
 *   1. Ensure a `professionals` row exists (Mission Control lists pending
 *      applications from this table — Auth-only users are invisible).
 *   2. Stamp `email_verified_at`.
 *   3. Fire `send-pro-application-received` (pro "under review" + ops
 *      notify to prosign@golfauth.com). Idempotent via
 *      application_received_email_sent_at.
 *
 * Product login still requires Mission Control `approval_status =
 * approved` (ProtectedRoute). The DB trigger
 * `create_professional_from_auth` only inserts once Auth email is
 * confirmed; client insert remains the reliable path when triggers
 * can't attach on hosted projects.
 */
import { supabase } from './supabase';
import { clearProOtpFailures } from './proOtpAttempts';

export type ProSignupFields = {
  email: string;
  first_name: string;
  last_name: string;
  company?: string | null;
  facility?: string | null;
  street_address?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  phone?: string | null;
  booking_url?: string | null;
  brands?: string[] | null;
  image?: string | null;
  certification_number?: string | null;
};

export function proFieldsFromUserMetadata(
  user: { email?: string | null; user_metadata?: Record<string, unknown> | null },
): ProSignupFields {
  const m = user.user_metadata ?? {};
  const certifications = m.certifications;
  return {
    email: (user.email ?? '').toLowerCase().trim(),
    first_name: String(m.first_name ?? ''),
    last_name: String(m.last_name ?? ''),
    company: (m.company as string | null) ?? null,
    facility: (m.facility as string | null) ?? null,
    street_address:
      (m.street_address as string | null) ??
      (m.address as string | null) ??
      null,
    suburb: (m.suburb as string | null) ?? null,
    state: (m.state as string | null) ?? null,
    postcode: (m.postcode as string | null) ?? null,
    country: (m.country as string | null) || 'Australia',
    phone: (m.phone as string | null) ?? null,
    booking_url: (m.booking_url as string | null) ?? null,
    brands: Array.isArray(m.brands) ? (m.brands as string[]) : [],
    image: (m.image as string | null) ?? null,
    certification_number: Array.isArray(certifications)
      ? (certifications as string[]).filter((c) => String(c).trim()).join(', ') || null
      : null,
  };
}

export async function ensureProfessionalRow(
  userId: string,
  fields: ProSignupFields,
): Promise<{ created: boolean }> {
  const verifiedAt = new Date().toISOString();
  const { data: existing } = await supabase
    .from('professionals')
    .select('id, email_verified_at')
    .eq('id', userId)
    .maybeSingle();
  if (existing) {
    if (!existing.email_verified_at) {
      await supabase
        .from('professionals')
        .update({ email_verified_at: verifiedAt })
        .eq('id', userId);
    }
    return { created: false };
  }

  const { error } = await supabase.from('professionals').insert({
    id: userId,
    email: fields.email,
    first_name: fields.first_name,
    last_name: fields.last_name,
    company: fields.company || null,
    facility: fields.facility || null,
    street_address: fields.street_address || null,
    suburb: fields.suburb || null,
    state: fields.state || null,
    postcode: fields.postcode || null,
    country: fields.country || 'Australia',
    phone: fields.phone || null,
    booking_url: fields.booking_url || null,
    brands: fields.brands || [],
    image: fields.image || null,
    certification_number: fields.certification_number || null,
    email_verified_at: verifiedAt,
  });

  if (error && error.code !== '23505' && !error.message?.includes('duplicate')) {
    console.error('[completeProSignup] professionals insert failed:', error);
    throw error;
  }
  return { created: !error };
}

/**
 * Notify the pro (under review) + ops inbox. Non-fatal on failure —
 * row creation must still succeed so Mission Control can approve.
 * Retries once after a short delay (covers insert-replication races
 * that 404 "Professional row not found").
 */
export async function notifyProApplicationReceived(): Promise<void> {
  const attempt = async () =>
    supabase.functions.invoke('send-pro-application-received', { body: {} });

  try {
    let { data, error } = await attempt();
    const missingRow =
      !!error &&
      (String((data as { error?: string } | null)?.error ?? '')
        .toLowerCase()
        .includes('not found') ||
        String(error.message ?? '').toLowerCase().includes('not found'));
    if (missingRow) {
      await new Promise((r) => setTimeout(r, 750));
      ({ data, error } = await attempt());
    }
    if (error) {
      console.warn(
        '[completeProSignup] application-received email failed (non-fatal):',
        error,
        data,
      );
    }
  } catch (err) {
    console.warn(
      '[completeProSignup] application-received invoke threw (non-fatal):',
      err,
    );
  }
}

/** Ensure row + fire application-received emails. */
export async function completeProSignupAfterAuth(
  userId: string,
  fields: ProSignupFields,
): Promise<void> {
  await ensureProfessionalRow(userId, fields);
  // Successful email verification — drop any prior OTP failure tally.
  if (fields.email) {
    await clearProOtpFailures(fields.email);
  }
  // Brief yield so PostgREST/RLS sees the just-inserted row before the
  // edge function looks it up by JWT subject.
  await new Promise((r) => setTimeout(r, 200));
  await notifyProApplicationReceived();
}
